import { PluginKey, Plugin, EditorState } from 'prosemirror-state'
import { MarkType, Node } from 'prosemirror-model'
import { InitConfig } from './Types'
import { schema, TrackedChange } from 'prose-core'
import { throttle } from 'lodash'
import { type EditorView } from 'prosemirror-view'

const SYNC_INTERVAL = 1000

const { deletion, insertion, formatting_change, comment, task } = schema.marks

const defaultCollabMarks = [deletion, insertion, formatting_change, comment, task]

class CollabItemsExtractorView {
  view: EditorView
  pluginKey: PluginKey

  constructor(view: EditorView, pluginKey: PluginKey) {
    this.view = view
    this.pluginKey = pluginKey
  }

  sync = throttle(() => {
    const { store } = this.pluginKey.getState(this.view.state)

    store.setOrderedCollabItemsUuids(extractCollabItemsUuids(this.view.state.doc))
  }, SYNC_INTERVAL)

  update = (view: EditorView, prevState: EditorState) => {
    this.view = view
    if (this.view.state.doc.eq(prevState.doc)) {
      return
    }

    this.sync()
  }
}

export function extractCollabItemsUuids(node: Node, markTypes?: MarkType[]) {
  const includedTypes = markTypes || defaultCollabMarks
  const uuids: Set<string> = new Set()

  node.descendants((node) => {
    node.marks.forEach((mark) => {
      if (includedTypes.includes(mark.type)) {
        uuids.add(mark.attrs.uuid)
      }
    })

    if (!markTypes) {
      node.attrs.trackedChanges?.forEach((trackedChange: TrackedChange) => uuids.add(trackedChange.uuid))
      if (node.type === schema.nodes.list_item && node.attrs.todoId) {
        uuids.add(node.attrs.uuid)
      }
      /**
       * Images and other nodes store comments and other objects uuids in their `uuids` attribute.
       */
      node.attrs.uuids?.forEach((uuid: string) => uuids.add(uuid))
    }
  })

  return uuids
}

/**
 * This plugin extracts the uuids from nodes that are attached to collaboration
 * items (tracked changes, comments and tasks), and syncs them with the store
 * passed as config. The uuids are ordered based on their position in the
 * document.
 */
export function collabItemsExtractor(config: InitConfig) {
  return new Plugin({
    key: config.key,
    state: {
      init(_, state) {
        config.store.setOrderedCollabItemsUuids(extractCollabItemsUuids(state.doc, config.markTypes))
        return {
          store: config.store,
        }
      },
      apply(_, state) {
        return state
      },
    },
    view(view) {
      return new CollabItemsExtractorView(view, config.key)
    },
  })
}
